import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Send } from "lucide-react";
import api, { TIER_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const login = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function BattleJoin() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [battle, setBattle] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    (async () => {
      try { setBattle((await api.get(`/battles/invite/${token}`)).data); }
      catch (e) { setErr(true); }
    })();
  }, [token, user, loading]);

  const accept = async () => {
    if (!content.trim()) return toast.error("Add your side first");
    setBusy(true);
    try {
      await api.post(`/battles/${battle.id}/accept`, { content_type: "text", content });
      toast.success("Challenge accepted! Both sides are now in the queue 🔥");
      navigate("/app/battles");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/20 blur-[120px] pointer-events-none" />
      <div className="relative z-10 max-w-lg mx-auto px-6 py-16" data-testid="battle-join">
        <div className="flex items-center gap-2 text-brand mb-4"><Swords size={22} /><span className="text-xs tracking-widest uppercase font-bold">Roast-Off challenge</span></div>
        {!user ? (
          <>
            <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl">You've been challenged</h1>
            <p className="text-zinc-400 mt-3 mb-6">Sign in to accept the Roast-Off. You'll both get roasted independently, then the crowd picks who took it best.</p>
            <button data-testid="join-login-btn" onClick={login} className="px-6 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold">Sign in to accept</button>
          </>
        ) : err ? (
          <div className="text-zinc-400" data-testid="join-error">This challenge isn't available anymore.</div>
        ) : !battle ? (
          <div className="text-zinc-400">Loading…</div>
        ) : battle.status !== "pending_acceptance" ? (
          <div data-testid="join-taken" className="text-zinc-400">This challenge has already been {battle.status === "declined" ? "declined" : "accepted"}.</div>
        ) : (
          <>
            <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl">{battle.challenger_name} challenged you</h1>
            <p className="text-zinc-400 mt-3 mb-5">Tier: <b style={{ color: (TIER_META[battle.tier] || TIER_META.savage).color }}>{(TIER_META[battle.tier] || TIER_META.savage).label}</b>. Free to enter — add your own content to get roasted, then both winning roasts get paired into a shareable Battle Card.</p>
            <textarea data-testid="join-content" rows={3} maxLength={600} value={content} onChange={(e) => setContent(e.target.value)} placeholder="What should roasters roast about you?"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-sm focus:border-brand focus:outline-none resize-none" />
            <button data-testid="accept-battle-btn" onClick={accept} disabled={busy} className="mt-3 w-full py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold disabled:opacity-50">
              {busy ? "Accepting…" : <>Accept challenge (free) <Send size={15} className="inline ml-1" /></>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
